import React, { useState } from 'react';
import { Box, Button, useMediaQuery, useTheme } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { AppointmentStatus } from '../../../types/appointment.types';
import { FilterToggleGroup } from '../../common/FilterToggleGroup';
import { PageToolbar } from '../../common/PageToolbar';
import { PageCard } from '../../common/PageCard';
import { UniversalPagination } from '../../common/UniversalPagination';
import { useAuth } from '../../../hooks/useAuth';
import { useExpertAppointments } from './hooks/useExpertAppointments';
import { useExpertAppointmentActions } from './hooks/useExpertAppointmentActions';
import { useAppointmentDeepLink } from '../hooks/useAppointmentDeepLink';
import { useRefetchOnNotification } from '../hooks/useRefetchOnNotification';
import { ExpertAppointmentsTable } from './ExpertAppointmentsTable';
import { ExpertAppointmentDetailsDialog } from './dialogs/ExpertAppointmentDetailsDialog';
import { ExpertAppointmentCancelDialog } from './dialogs/ExpertAppointmentCancelDialog';
import { ExpertAppointmentCompleteDialog } from './dialogs/ExpertAppointmentCompleteDialog';
import { ExpertAppointmentOutcomeDialog } from './dialogs/ExpertAppointmentOutcomeDialog';
import { CreateAppointmentByExpertDialog } from './CreateAppointmentByExpertDialog';
import { RescheduleAppointmentDialog } from '../RescheduleAppointmentDialog';

const BASE_STATUS_OPTIONS = [
  { value: '',                            label: 'Всі' },
  { value: AppointmentStatus.PENDING,     label: 'Очікує' },
  { value: AppointmentStatus.CONFIRMED,   label: 'Підтверджено' },
  { value: AppointmentStatus.COMPLETED,   label: 'Завершено' },
  { value: AppointmentStatus.CANCELLED,   label: 'Скасовано' },
];

interface Props {
  clientId?: number;
}

export const ExpertAppointmentsList: React.FC<Props> = ({ clientId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();

  const [createOpen, setCreateOpen] = useState(false);

  const {
    appointments, total, loading, options, fetchAppointments,
    statusFilter, clientFilter,
    onStatusChange, onClientChange,
    page, limit, onPageChange, onLimitChange,
    handleReset,
  } = useExpertAppointments(clientId);

  const pendingCount = options.statusCounts?.pending ?? 0;

  const actions = useExpertAppointmentActions(fetchAppointments);

  // Live-оновлення списку при отриманні нової notification (наприклад, клієнт скасував).
  useRefetchOnNotification(fetchAppointments);

  // Deep-link: URL `?id=...` ↔ модалка деталей. Дозволяє ділитися посиланнями на конкретну зустріч.
  const { openWithUrl: openDetailsWithUrl, closeWithUrl: closeDetailsWithUrl } =
    useAppointmentDeepLink(actions.selected?.id ?? null, actions.handleOpenDetails, actions.handleCloseDetails);

  const clientOptions = options.clients.map(c => ({ value: String(c.id), label: c.name }));
  const preselectedClient = clientId
    ? options.clients.find(c => c.id === clientId) ?? null
    : null;

  const statusOptions = BASE_STATUS_OPTIONS.map(opt =>
    opt.value === AppointmentStatus.PENDING
      ? { ...opt, badgeCount: pendingCount }
      : opt,
  );

  const toolbarFilters = [
    ...(!clientId && clientOptions.length > 0 ? [{
      id: 'clientId',
      type: 'select' as const,
      label: 'Клієнт',
      value: clientFilter,
      onChange: onClientChange,
      options: clientOptions,
    }] : []),
    {
      id: 'status',
      type: 'custom' as const,
      value: statusFilter,
      onChange: onStatusChange,
      renderCustom: () => (
        <FilterToggleGroup
          value={statusFilter}
          onChange={onStatusChange}
          options={statusOptions}
        />
      ),
    },
  ];

  return (
    <Box>
      <PageCard sx={{ mb: 2 }}>
        <PageToolbar
          storageKey={clientId ? `filters:client-${clientId}-appointments` : 'filters:expert-appointments'}
          filters={toolbarFilters}
          onReset={handleReset}
          showReset
          extraActions={
            user && (
              <Button
                variant="contained"
                onClick={() => setCreateOpen(true)}
                sx={{
                  minWidth: isMobile ? '40px' : 'auto',
                  p: isMobile ? 1 : undefined,
                  height: '40px',
                }}
              >
                <AddIcon sx={{ mr: isMobile ? 0 : 1 }} />
                {!isMobile && 'Нова зустріч'}
              </Button>
            )
          }
        />
      </PageCard>

      <PageCard sx={{ p: 0, overflow: 'hidden' }}>
        <ExpertAppointmentsTable
          appointments={appointments}
          loading={loading}
          isMobile={isMobile}
          showClient={!clientId}
          onOpenDetails={openDetailsWithUrl}
          onQuickConfirm={actions.handleConfirm}
          onOpenComplete={actions.openCompleteDialog}
          onOpenReschedule={actions.openRescheduleDialog}
          onOpenCancel={actions.openCancelDialog}
        />
        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <UniversalPagination
            page={page}
            count={Math.ceil(total / limit)}
            limit={limit}
            total={total}
            onPageChange={onPageChange}
            onLimitChange={onLimitChange}
            storageKey={clientId ? `pagination:client-${clientId}-appointments` : 'pagination:expert-appointments'}
          />
        </Box>
      </PageCard>

      {user && (
        <CreateAppointmentByExpertDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onSuccess={() => { setCreateOpen(false); fetchAppointments(); }}
          expertId={user.id}
          clients={options.clients}
          preselectedClient={preselectedClient ?? undefined}
        />
      )}

      <ExpertAppointmentDetailsDialog
        selected={actions.selected}
        modalLoading={actions.modalLoading}
        onClose={closeDetailsWithUrl}
        onConfirm={actions.handleConfirm}
        onOpenCancel={actions.openCancelDialog}
        onOpenComplete={actions.openCompleteDialog}
        onOpenReschedule={actions.openRescheduleDialog}
        onOpenOutcome={actions.openOutcomeDialog}
      />

      <ExpertAppointmentCancelDialog
        target={actions.cancelTarget}
        cancelling={actions.cancelling}
        onConfirm={actions.handleCancel}
        onClose={actions.closeCancelDialog}
      />

      <ExpertAppointmentCompleteDialog
        target={actions.completeTarget}
        completing={actions.completing}
        onConfirm={actions.handleComplete}
        onClose={actions.closeCompleteDialog}
      />

      <ExpertAppointmentOutcomeDialog
        open={actions.outcomeOpen}
        initialText={actions.outcomeInitialText}
        saving={actions.savingOutcome}
        onSave={actions.handleSaveOutcome}
        onClose={actions.closeOutcomeDialog}
      />

      {actions.rescheduleTarget && (
        <RescheduleAppointmentDialog
          open
          onClose={actions.closeRescheduleDialog}
          onSuccess={() => {
            actions.closeRescheduleDialog();
            if (actions.selected?.id === actions.rescheduleTarget?.id) actions.handleCloseDetails();
            fetchAppointments();
          }}
          appointmentId={actions.rescheduleTarget.id}
          expertId={actions.rescheduleTarget.expert.id}
          expertName={actions.rescheduleTarget.client.name}
          actorRole="expert"
        />
      )}
    </Box>
  );
};
